import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SockJS from 'sockjs-client';
import { Stomp } from 'stompjs/lib/stomp';
import { SERVICE } from '../../config/devices';
import { SERVICE as WS_SERVICE } from '../../config/websocket';
import { selectors as authSelectors } from '../../modules/auth';
import { selectors as companySelectors } from '../../modules/company';
import { selectors as siteSelectors } from '../../modules/sites';
import actions from '../../modules/websocket';
import { OryLoginStatus } from '../../ory/types';

const subscriptions = (companyId, siteId) => [
  {
    destination: `/notification/companies/${companyId}/sites/${siteId}/devices`,
    id: WS_SERVICE.DEVICE_CHANGE,
    service: WS_SERVICE.DEVICE_CHANGE,
  },
  {
    destination: `/notification/companies/${companyId}/sites/${siteId}/services/${SERVICE.REMOTE_CONFIGURATION}/configurations`,
    id: WS_SERVICE.REMOTE_CONFIGURATION_DEVICE_CHANGE,
    service: WS_SERVICE.REMOTE_CONFIGURATION_DEVICE_CHANGE,
  },
  {
    destination: `/notification/companies/${companyId}/payments`,
    id: WS_SERVICE.SUBSCRIPTION_PAYMENT,
    service: WS_SERVICE.SUBSCRIPTION_PAYMENT,
  },
];

const stompDebug =
  process.env.REDUX_DEBUG === '1'
    ? (frame) => {
        // eslint-disable-next-line no-console
        console.log('[STOMP]', frame);
      }
    : null;

const dispatchMessage = (message, availableSubscriptions, dispatch) => {
  const destination = message?.headers?.destination;
  if (!destination) {
    return;
  }
  const subscription = availableSubscriptions.find((sub) => sub.destination === destination);
  if (subscription) {
    dispatch(actions.listeningFromSocket(subscription.service, JSON.parse(message.body)));
  }
};

const subscribeAll = (client, availableSubscriptions, headers, dispatch) => {
  for (const subscription of availableSubscriptions) {
    client.unsubscribe(subscription.id);
  }
  for (const subscription of availableSubscriptions) {
    client.subscribe(
      subscription.destination,
      (message) => dispatchMessage(message, availableSubscriptions, dispatch),
      { ...headers, id: subscription.id }
    );
  }
};

export default function WebSocket() {
  const dispatch = useDispatch();
  const siteId = useSelector(siteSelectors.getSiteId);
  const companyId = useSelector(companySelectors.getCompanyId);
  const accessToken = useSelector(authSelectors.getAccessTokenSelector);
  const oryLoginStatus = useSelector(authSelectors.getOryLoginStatusSelector);

  useEffect(() => {
    if (!((accessToken || oryLoginStatus === OryLoginStatus.SessionConfirmed) && companyId && siteId)) {
      return undefined;
    }

    let client;

    const connect = () => {
      const socketToken = accessToken;

      const sock = new SockJS(process.env.SOCKET_URL);
      client = new Stomp.over(sock);
      client.debug = stompDebug;

      const availableSubscriptions = subscriptions(companyId, siteId);
      const headers = socketToken ? { 'X-Api-Key': socketToken } : {};
      client.connect(headers, () => subscribeAll(client, availableSubscriptions, headers, dispatch));
    };

    connect();

    return () => {
      if (client && client.connected) {
        client.disconnect();
      }
    };
  }, [accessToken, oryLoginStatus, companyId, siteId]);
  return null;
}
